import { ChevronDown, Layers } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'

import {
  ComposerAppearance,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  composerTriggerClassName,
} from './composerAppearance'

/**
 * Knowledge-scope chip — the composer door onto `KnowledgeScopePicker`.
 *
 * A JOIN, like `EffortToggle` beside it: the Home Lab composer, the Threads
 * lobby composer and the thread under-row all mounted a byte-identical inline
 * button before this file existed, so an icon added to one of them would have
 * silently skipped the other two. The only things that ever varied are the
 * label, the appearance and the test id, which are therefore the props.
 *
 * The icon is `Layers`, not a folder or a globe. The chip's value spans BOTH
 * channels (`Any` = web + library) as well as a filtered set of library items,
 * so an icon that pictures one channel would misname the default. `Globe` is
 * additionally spoken for one control along the same row, by the language
 * swapper.
 *
 * `label` is computed by `scopeChipLabel` at every call site rather than here:
 * the thread header chip uses its own variant of that helper, and folding the
 * choice into this component would hide which one a surface gets.
 */

interface ScopeChipProps {
  label: string
  onClick: () => void
  appearance?: ComposerAppearance
  testId: string
  className?: string
}

export function ScopeChip(props: ScopeChipProps) {
  const { label, onClick, appearance = 'external', testId, className } = props

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Sources: ${label}`}
      className={cn(composerTriggerClassName(appearance), className)}
      data-testid={testId}
    >
      <Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className={composerTriggerCaptionClassName(appearance)}>{label}</span>
      <ChevronDown
        className={cn(
          'h-3 w-3 opacity-70',
          composerTriggerChevronClassName(appearance),
        )}
        aria-hidden="true"
      />
    </button>
  )
}
